// C:\manoj\webrestaurant\frontend\restaurant-pos-FE\src\components\Bearer\ClosingEntry.jsx
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  ArrowLeft,
  Home,
  Truck,
  Armchair,
  ChefHat,
  CircleDollarSign,
  BarChart3,
  LogOut,
  CircleUser,
  Power,
  CircleAlert,
  Check,
  X,
  Bell
} from "lucide-react";
import { UserContext } from '../../Context/UserContext';
import CurrencySymbol, { resolveCurrencyCode } from '../CurrencySymbol';
// Import Bootstrap CSS
import 'bootstrap/dist/css/bootstrap.min.css';

function ClosingEntryWithNavbar() {
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const userData = useSelector((state) => state.user);
  const storedUser = JSON.parse(localStorage.getItem('user')) || { email: "Guest" };
  const currentUser = userData?.user || storedUser;

  const [currentTime, setCurrentTime] = useState(new Date());
  const [baseUrl, setBaseUrl] = useState('');

  // Notifications state
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const userObj = currentUser;
        if (!userObj || !userObj.role) return;

        const safeBaseUrl = baseUrl || '';
        const notifUrl = `${safeBaseUrl}/api/notifications`;
        const permUrl = `${safeBaseUrl}/api/role-permissions?role=${encodeURIComponent(userObj.role)}`;

        const [notifRes, permRes] = await Promise.all([
          axios.get(notifUrl).catch(() => ({ data: [] })),
          axios.get(permUrl).catch(() => ({ data: { permissions: [] } }))
        ]);

        const allNotifs = notifRes.data || [];
        const perms = permRes.data?.permissions || [];
        const leaveNotifPerm = perms.find(p => p.pageId === 'leave_apply_notif');
        const scheduleNotifPerm = perms.find(p => p.pageId === 'schedule_assign_notif');

        const isAdmin = userObj.role?.toLowerCase().includes('admin') || userObj.is_test;
        const userEmpId = String(userObj.employeeId || userObj.employeeIdCode || userObj.id);

        const currentUserId = String(userObj.id || userObj._id);
        const filtered = allNotifs.filter(n => {
          if (n.deleted_by && n.deleted_by.includes(currentUserId)) return false;
          if (n.read_by && n.read_by.includes(currentUserId)) return false;

          if (isAdmin) return true;

          if (String(n.employeeId) === userEmpId || String(n.userId) === String(userObj.id)) {
            return true;
          }

          const category = n.category || 'general';
          if (category === 'leave') {
            if (!leaveNotifPerm || !leaveNotifPerm.canRead) return false;
            if (leaveNotifPerm.dataAccess === 'OWN') return false;
            return true;
          }
          if (category === 'schedule') {
            if (!scheduleNotifPerm || !scheduleNotifPerm.canRead) return false;
            if (scheduleNotifPerm.dataAccess === 'OWN') return false;
            return true;
          }
          return true;
        });
        setUnreadCount(filtered.length);
      } catch (err) {
        // Silently handle error
      }
    };
    if (baseUrl !== undefined && baseUrl !== '') {
      fetchUnread();
      const interval = setInterval(fetchUnread, 120000);
      window.addEventListener('notificationUpdate', fetchUnread);
      return () => {
        clearInterval(interval);
        window.removeEventListener('notificationUpdate', fetchUnread);
      };
    }
  }, [currentUser, baseUrl]);

  // Permission State
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canCreate, setCanCreate] = useState(false);

  // System settings state
  const [settings, setSettings] = useState({
    country: 'India',
    language: 'English',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    currency: 'INR',
    useCurrencySymbol: false,
    dateFormat: 'yyyy-mm-dd',
    timeFormat: 'hh:mm a',
    numberFormat: '#,##,###.##',
    useNumberFormatFromCurrency: false,
    firstDayOfWeek: 'Monday',
    floatPrecision: 3,
    currencyPrecision: 4,
  });

  const [warningMessage, setWarningMessage] = useState("");
  const [warningType, setWarningType] = useState("warning");
  const [pendingAction, setPendingAction] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch Network Info for BaseUrl
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get("/api/network_info");
        const { config: appConfig } = response.data;
        if (appConfig.mode === "client") {
          setBaseUrl(`http://${appConfig.server_ip}:6034`);
        } else {
          setBaseUrl('');
        }
      } catch (error) {
        console.error("Failed to fetch network config:", error);
        setBaseUrl('');
      }
    };
    fetchConfig();
  }, []);

  // Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userObj = JSON.parse(userStr);
          const role = userObj.role;
          if (role) {
            const url = `${baseUrl}/api/role-permissions?role=${role}`;
            const response = await axios.get(url);
            const perms = response.data.permissions || [];
            const pagePerm = perms.find(p => p.pageId === 'closing');
            if (pagePerm) {
              const cRead = pagePerm.canRead;
              const cWrite = pagePerm.canWrite;
              const cDelete = pagePerm.canDelete;
              const cCreate = pagePerm.canCreate;

              setCanRead(cRead === true || cRead === 1 || cRead === 'true' || cRead === '1' || !!cRead);
              setCanWrite(cWrite === true || cWrite === 1 || cWrite === 'true' || cWrite === '1' || !!cWrite);
              setCanDelete(cDelete === true || cDelete === 1 || cDelete === 'true' || cDelete === '1' || !!cDelete);
              setCanCreate(cCreate === true || cCreate === 1 || cCreate === 'true' || cCreate === '1' || !!cCreate);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    };
    if (baseUrl !== undefined) fetchPermissions();
  }, [baseUrl]);

  // Fetch system settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/settings`);
        setSettings((prev) => ({ ...prev, ...response.data }));
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    if (baseUrl !== undefined) fetchSettings();
  }, [baseUrl]);

  const getFormattedDate = (date, dateFormat) => {
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const formattedDate = getFormattedDate(currentTime, 'dd-mm-yyyy');
  const formattedTime = currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

  const getCurrencyFormatter = (invoiceCurrency = null, invoicePrecision = null, asHtml = false, asReact = false) => {
    // Resolve currency from active context using currency_map if not explicitly provided
    const activeComp = localStorage.getItem('active_company') || '';
    const activeBranch = localStorage.getItem('active_branch') || '';
    const currency = invoiceCurrency || resolveCurrencyCode(null, activeComp, activeBranch);
    const precision = invoicePrecision !== null ? parseInt(invoicePrecision) : parseInt(settings.currencyPrecision) || 2;
    return {
      format: (value) => {
        const numValue = parseFloat(value) || 0;
        const fixedValue = numValue.toFixed(precision);
        if (asReact) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <span style={{ marginRight: '2px', transform: 'translateY(1px)' }}>
                <CurrencySymbol currencyCode={currency} size={14} />
              </span>
              {fixedValue}
            </span>
          );
        } else if (asHtml) {
          if (currency?.toUpperCase() === 'AED') {
            return `<img src="/assets/Dirham Currency Symbol - Black.svg" style="height: 12px; vertical-align: middle; margin-right: 2px; filter: brightness(0.1);" alt="AED"/> ${fixedValue}`;
          } else {
            const symbols = {
              INR: "₹", USD: "$", EUR: "€", GBP: "£", JPY: "¥", CNY: "¥",
              SGD: "S$", MYR: "RM", THB: "฿", IDR: "Rp", KRW: "₩", PHP: "₱", SAR: "﷼",
              QAR: "﷼", KWD: "د.ك", OMR: "﷼", BHD: ".د.ب", CAD: "CA$", AUD: "A$", NZD: "NZ$",
              CHF: "CHF", ZAR: "R", BRL: "R$", PKR: "₨", LKR: "Rs", NGN: "₦"
            };
            return `${symbols[currency?.toUpperCase()] || currency} ${fixedValue}`;
          }
        } else {
          const symbols = {
            INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", JPY: "¥", CNY: "¥",
            SGD: "S$", MYR: "RM", THB: "฿", IDR: "Rp", KRW: "₩", PHP: "₱", SAR: "﷼",
            QAR: "﷼", KWD: "د.ك", OMR: "﷼", BHD: ".د.ب", CAD: "CA$", AUD: "A$", NZD: "NZ$",
            CHF: "CHF", ZAR: "R", BRL: "R$", PKR: "₨", LKR: "Rs", NGN: "₦"
          };
          return `${symbols[currency?.toUpperCase()] || currency} ${fixedValue}`;
        }
      }
    };
  };

  const formatCurrency = (amount) => {
    return getCurrencyFormatter(null, 2, false, true).format(amount);
  };

  const handleWarningOk = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setWarningMessage("");
    setWarningType("warning");
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    navigate('/', { replace: true });
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const [posOpeningEntry, setPosOpeningEntry] = useState(state?.posOpeningEntry || localStorage.getItem('posOpeningEntry') || '');
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [postingDate, setPostingDate] = useState(new Date().toISOString().split('T')[0]);
  const [periodEndDate, setPeriodEndDate] = useState(new Date().toISOString().slice(0, 16));
  const [company, setCompany] = useState('');
  const [user, setUserState] = useState('');
  const [posProfile, setPosProfile] = useState('');
  const [posTransactions, setPosTransactions] = useState([{ pos_invoice: '', grand_total: '', posting_date: '', customer: '' }]);
  const [paymentReconciliation, setPaymentReconciliation] = useState([]);
  const [taxes, setTaxes] = useState([{ account_head: '', rate: '', amount: '' }]);
  const [grandTotal, setGrandTotal] = useState('');
  const [netTotal, setNetTotal] = useState('');
  const [totalQuantity, setTotalQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [openingEntries, setOpeningEntries] = useState([]);

  useEffect(() => {
    const reduxPosProfile = userData?.pos_profile || localStorage.getItem('pos_profile') || 'POS-001';
    const reduxUserEmail = currentUser.email || 'bearer@gmail.com';
    const reduxCompany = userData?.company || localStorage.getItem('company') || 'MyCompany';
    setPosProfile(reduxPosProfile);
    setUserState(reduxUserEmail);
    setCompany(reduxCompany);
  }, [userData, currentUser]);

  useEffect(() => {
    const fetchOpeningEntries = async () => {
      if (!posProfile) return;
      try {
        const response = await axios.post(`${baseUrl}/api/get_pos_opening_entries`, { pos_profile: posProfile });
        if (response.data.status === 'success' && Array.isArray(response.data.message)) {
          setOpeningEntries(response.data.message);
        }
      } catch (error) {
        console.error('Error fetching POS Opening Entries:', error);
      }
    };
    if (posProfile && baseUrl !== undefined) fetchOpeningEntries();
  }, [posProfile, baseUrl]);

  useEffect(() => {
    const fetchPosDetails = async () => {
      if (!posOpeningEntry || !openingEntries.length) return;
      const selectedEntry = openingEntries.find(entry => entry.name === posOpeningEntry);
      if (!selectedEntry) return;

      setPeriodStartDate(selectedEntry.period_start_date?.split(' ')[0] || '');
      setPostingDate(selectedEntry.posting_date || new Date().toISOString().split('T')[0]);
      setCompany(selectedEntry.company || '');
      setUserState(selectedEntry.user || currentUser.email || 'bearer@gmail.com');
      setPosProfile(selectedEntry.pos_profile || '');

      const balanceDetails = selectedEntry.balance_details || [];
      if (balanceDetails.length > 0) {
        setPaymentReconciliation(balanceDetails.map(detail => ({
          mode_of_payment: detail.mode_of_payment || '',
          opening_amount: detail.opening_amount ? detail.opening_amount.toString() : '0',
          expected_amount: detail.opening_amount ? detail.opening_amount.toString() : '0',
          closing_amount: ''
        })));
      }

      try {
        const response = await axios.post(`${baseUrl}/api/get_pos_invoices`, { pos_opening_entry: posOpeningEntry });
        if (response.data.message && response.data.message.status === 'success') {
          const invoices = response.data.message.invoices || [];
          setPosTransactions(invoices.length > 0 ? invoices.map(inv => ({
            pos_invoice: inv.pos_invoice || '',
            grand_total: inv.grand_total || '',
            posting_date: inv.posting_date || '',
            customer: inv.customer || ''
          })) : [{ pos_invoice: '', grand_total: '', posting_date: '', customer: '' }]);
            
            const salesByMode = response.data.message.sales_by_mode || {};
            setPaymentReconciliation(prev => {
                const modeMap = {};
                Object.keys(salesByMode).forEach(k => {
                    const normalizedMode = k.trim().charAt(0).toUpperCase() + k.trim().slice(1).toLowerCase();
                    modeMap[normalizedMode] = (modeMap[normalizedMode] || 0) + salesByMode[k];
                });
                
                const prevMap = {};
                prev.forEach(pr => {
                    let mode = pr.mode_of_payment || 'Cash';
                    mode = mode.trim().charAt(0).toUpperCase() + mode.trim().slice(1).toLowerCase();
                    
                    if (!prevMap[mode]) {
                        prevMap[mode] = { opening: parseFloat(pr.opening_amount) || 0, closing: pr.closing_amount };
                    } else {
                        prevMap[mode].opening += parseFloat(pr.opening_amount) || 0;
                        if (pr.closing_amount && !prevMap[mode].closing) prevMap[mode].closing = pr.closing_amount;
                    }
                });
                
                const newPr = [];
                const allModes = new Set([...Object.keys(modeMap), ...Object.keys(prevMap)]);
                
                allModes.forEach(mode => {
                    const openingAmt = prevMap[mode] ? prevMap[mode].opening : 0;
                    const closingAmt = prevMap[mode] ? prevMap[mode].closing : '';
                    const salesAmt = modeMap[mode] || 0;
                    
                    let expected = 0;
                    if (mode === 'Cash') {
                        expected = openingAmt + salesAmt;
                    } else {
                        expected = salesAmt;
                    }
                    
                    newPr.push({
                        mode_of_payment: mode,
                        opening_amount: openingAmt.toString(),
                        expected_amount: expected.toString(),
                        closing_amount: closingAmt,
                        sales_amount: salesAmt
                    });
                });
                
                return newPr;
            });

          setTaxes(response.data.message.taxes?.length ? response.data.message.taxes.map(tax => ({
            account_head: tax.account_head || '',
            rate: tax.rate || '',
            amount: tax.amount || ''
          })) : [{ account_head: '', rate: '', amount: '' }]);

          setGrandTotal(response.data.message.grand_total || '');
          setNetTotal(response.data.message.net_total || '');
          setTotalQuantity(response.data.message.total_quantity || '');
        }
      } catch (error) {
        console.error('Error fetching POS Invoices:', error);
      }
    };
    if (posOpeningEntry && baseUrl !== undefined) fetchPosDetails();
  }, [posOpeningEntry, openingEntries, currentUser.email, baseUrl]);

  const handleAddPosTransaction = () => {
    if (!canWrite) {
      setWarningMessage("You do not have permission to modify transactions.");
      return;
    }
    setPosTransactions(prev => [...prev, { pos_invoice: '', grand_total: '', posting_date: '', customer: '' }]);
  };

  const handlePosTransactionChange = (index, field, value) => {
    if (!canWrite) {
      setWarningMessage("You do not have permission to modify transactions.");
      return;
    }
    setPosTransactions(prev => prev.map((tx, i) => (i === index ? { ...tx, [field]: value } : tx)));
  };

  const handleRemovePosTransaction = (index) => {
    if (!canDelete) {
      setWarningMessage("You do not have permission to remove transactions.");
      return;
    }
    setPosTransactions(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddPaymentReconciliation = () => {
    if (!canWrite) {
      setWarningMessage("You do not have permission to modify payments.");
      return;
    }
    setPaymentReconciliation(prev => [...prev, { mode_of_payment: '', opening_amount: '0', expected_amount: '0', closing_amount: '' }]);
  };

  const handlePaymentReconciliationChange = (index, field, value) => {
    if (!canWrite) {
      setWarningMessage("You do not have permission to modify payments.");
      return;
    }
    setPaymentReconciliation(prev => prev.map((pr, i) => (i === index ? { ...pr, [field]: value } : pr)));
  };

  const handleRemovePaymentReconciliation = (index) => {
    if (!canDelete) {
      setWarningMessage("You do not have permission to remove payments.");
      return;
    }
    setPaymentReconciliation(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!canCreate) {
      setWarningMessage("You do not have permission to create closing entries.");
      return;
    }
      
      const missingClosingAmounts = paymentReconciliation.some(pr => !pr.closing_amount && pr.closing_amount !== 0);
      if (missingClosingAmounts) {
          setWarningMessage("Please enter the counted closing cash amount before submitting.");
          return;
      }

    setLoading(true);
    const payload = {
      pos_opening_entry: posOpeningEntry,
      posting_date: postingDate,
      period_end_date: periodEndDate,
      pos_transactions: posTransactions,
      payment_reconciliation: paymentReconciliation,
      taxes: taxes,
      grand_total: grandTotal,
      net_total: netTotal,
      total_quantity: totalQuantity,
    };

    try {
      const response = await axios.post(`${baseUrl}/api/create_closing_entry`, payload);
      if (response.data && response.data.message.status === 'success') {
        localStorage.removeItem('posOpeningEntry');
        localStorage.removeItem('openingEntryName');
        setWarningMessage("Session closed successfully. Go to login page?");
        setWarningType("success");
        setShowLogoutConfirm(true);
        setPendingAction(() => () => {
          navigate('/', { replace: true });
        });
      } else {
        setWarningMessage(response.data.message || 'Error occurred');
      }
    } catch (error) {
      console.error('ClosingEntry Error:', error);
      setWarningMessage(error.response?.data?.message || 'Network error occurred.');
    } finally {
      setLoading(false);
    }
  };

  if (!canRead && loading) return <div className="text-center mt-5">Checking Permissions...</div>;

  return (
    <div className="closing-entry-wrapper">
      <div className="navbar-fixed-container">
        {warningMessage && (
          <div className={`alert alert-${warningType} text-center alert-dismissible fade show`} role="alert">
            {warningMessage}
            {showLogoutConfirm ? (
              <div className="mt-2 text-center">
                <button type="button" className="btn btn-success me-3 px-4" onClick={() => { handleWarningOk(); setShowLogoutConfirm(false); }}>Yes</button>
                <button type="button" className="btn btn-secondary px-4" onClick={() => { setWarningMessage(""); setShowLogoutConfirm(false); }}>No</button>
              </div>
            ) : (
              <button type="button" className="btn btn-primary ms-3" onClick={handleWarningOk}>OK</button>
            )}
          </div>
        )}
        <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm" style={{ height: '80px', minHeight: '80px' }}>
          <div className="container-fluid">
            {/* Left Side: Back Button */}
            <div className="navbar-left-section d-flex align-items-center">
              <button
                className="back-nav-btn"
                onClick={() => navigate(-1)}
                title="Go Back"
              >
                <ArrowLeft size={20} />
                <span className="ms-2 d-none d-sm-inline">Back</span>
              </button>
            </div>
            <div className="collapse navbar-collapse">
              <ul className="navbar-nav mx-auto mb-2 mb-lg-0 d-flex justify-content-center">
                <li className="nav-item">
                  <a className={`nav-link ${location.pathname === '/frontpage' ? 'active' : ''}`} onClick={() => navigate('/frontpage')} title="Home">
                    <Home className="digital-icon" />
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${location.pathname === '/home' ? 'active' : ''}`} onClick={() => navigate('/home')} title="Delivery Type">
                    <Truck className="digital-icon" />
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${location.pathname === '/table' ? 'active' : ''}`} onClick={() => navigate('/table')} title="Table">
                    <Armchair className="digital-icon" />
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${location.pathname === '/kitchen' ? 'active' : ''}`} onClick={() => navigate('/kitchen')} title="Kitchen">
                    <ChefHat className="digital-icon" />
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${location.pathname === '/salespage' ? 'active' : ''}`} onClick={() => navigate('/salespage')} title="Sales">
                    <CircleDollarSign className="digital-icon" />
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${location.pathname === '/sales-reports' ? 'active' : ''}`} onClick={() => navigate('/sales-reports')} title="Reports">
                    <BarChart3 className="digital-icon" />
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${location.pathname === '/closing-entry' ? 'active' : ''}`} onClick={() => navigate('/closing-entry')} title="Closing">
                    <LogOut className="digital-icon" />
                  </a>
                </li>
              </ul>
              <div className="d-flex align-items-center ms-auto pe-3">
                <div
                  className="notification-nav-btn me-3"
                  onClick={() => navigate('/notifications')}
                  title="Notifications"
                  style={{
                    cursor: 'pointer',
                    position: 'relative',
                    backgroundColor: '#fff',
                    padding: '8px',
                    borderRadius: '50%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(52, 152, 219, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                >
                  <Bell size={24} style={{ color: '#3498db' }} />
                  {unreadCount > 0 && (
                    <span className="position-absolute" style={{
                      top: '-5px',
                      right: '-5px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      borderRadius: '50%',
                      minWidth: '18px',
                      height: '18px',
                      fontSize: '0.65rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid white',
                      boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)',
                      animation: 'pulse-notif 2s infinite',
                      zIndex: 10
                    }}>
                      {unreadCount}
                    </span>
                  )}
                </div>
                <div className="user-info text-end me-3">
                  <div className="d-flex align-items-center justify-content-end">
                    <CircleUser size={24} className="me-2 text-primary" />
                    <span className="fw-bold text-black mb-0">{currentUser.email}</span>
                  </div>
                  <small className="d-block text-muted fw-bold" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {formattedDate}:{formattedTime}
                  </small>
                </div>
                <div className="navbar-logout-btn-wrapper">
                  <a className="frontpage-header-logout" onClick={handleLogoutClick} title="Logout">
                    <Power className="digital-icon logout-icon" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>

      <div className="navbar-placeholder"></div>

      <div className="closing-entry-container">
        {/* Page Header */}
        <div className="ce-page-header">
          <div>
            <div className="ce-subtitle">Session Management</div>
            <h1>POS Closing Entry</h1>
          </div>
        </div>

        {!canRead ? (
          <div style={{ background: '#FEE2E2', color: '#991B1B', borderRadius: '12px', padding: '20px 24px', fontWeight: '600' }}>
            You do not have permission to view this page.
          </div>
        ) : (
          <>
            {/* Info Cards Grid */}
            <div className="ce-info-grid">
              <div className="ce-info-card">
                <span className="ce-info-label">POS Opening Entry</span>
                <select className="ce-info-select" value={posOpeningEntry} onChange={(e) => setPosOpeningEntry(e.target.value)}>
                  <option value="">-- Select --</option>
                  {openingEntries.map((entry) => (
                    <option key={entry._id} value={entry.name}>{entry.name}</option>
                  ))}
                </select>
              </div>
              <div className="ce-info-card">
                <span className="ce-info-label">Period Start Date</span>
                <input type="date" className="ce-info-input" value={periodStartDate} disabled />
              </div>
              <div className="ce-info-card">
                <span className="ce-info-label">Posting Date</span>
                <input type="date" className="ce-info-input" value={postingDate} onChange={(e) => setPostingDate(e.target.value)} />
              </div>
              <div className="ce-info-card">
                <span className="ce-info-label">Period End Date</span>
                <input type="datetime-local" className="ce-info-input" value={periodEndDate} onChange={(e) => setPeriodEndDate(e.target.value)} />
              </div>
              <div className="ce-info-card">
                <span className="ce-info-label">Company</span>
                <input type="text" className="ce-info-input" value={company} disabled />
              </div>
              <div className="ce-info-card">
                <span className="ce-info-label">User</span>
                <input type="text" className="ce-info-input" value={user} disabled />
              </div>
            </div>

            {/* POS Transactions Table */}
            <div className="ce-section-card">
              <div className="ce-section-header">
                <div className="ce-section-title">
                  POS Transactions
                  <span className="ce-section-badge">{posTransactions.length}</span>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="ce-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>POS Invoice</th>
                      <th>Grand Total</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#8A94A6', fontStyle: 'italic' }}>
                          No transactions found for this session
                        </td>
                      </tr>
                    ) : posTransactions.map((tx, index) => (
                      <tr key={index}>
                        <td style={{ color: '#8A94A6', fontWeight: '600', fontSize: '12px' }}>{index + 1}</td>
                        <td>
                          <input type="text" className="ce-table-input" value={tx.pos_invoice} disabled />
                        </td>
                        <td className="ce-amount-cell">{formatCurrency(tx.grand_total)}</td>
                        <td>
                          <input type="date" className="ce-table-input" value={tx.posting_date} disabled />
                        </td>
                        <td>
                          <input type="text" className="ce-table-input" value={tx.customer} disabled />
                        </td>
                        <td>
                          <button className="ce-remove-btn" onClick={() => handleRemovePosTransaction(index)} title="Remove">✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="ce-add-btn" onClick={handleAddPosTransaction}>
                + Add Transaction
              </button>
            </div>

            {/* Payment Reconciliation Table */}
            <div className="ce-section-card">
              <div className="ce-section-header">
                <div className="ce-section-title">
                  Payment Reconciliation
                  <span className="ce-section-badge">{paymentReconciliation.length}</span>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="ce-table">
                    <thead>
                      <tr>
                        <th>Payment Mode</th>
                        <th>Opening Amount</th>
                        <th>Expected Amount</th>
                        <th>Closing Amount</th>
                        <th>Difference</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                  <tbody>
                    {paymentReconciliation.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#8A94A6', fontStyle: 'italic' }}>
                          No payment modes found
                        </td>
                      </tr>
                    ) : paymentReconciliation.map((pr, index) => (
                      <tr key={index}>
                        <td>
                          <span className="ce-mode-badge">{pr.mode_of_payment || '—'}</span>
                        </td>
                        <td className="ce-amount-cell">{formatCurrency(pr.opening_amount)}</td>
                        <td className="ce-amount-cell">{formatCurrency(pr.expected_amount)}</td>
                        <td>
                          <input
                            type="number"
                            className="ce-table-input"
                            value={pr.closing_amount}
                            onChange={(e) => handlePaymentReconciliationChange(index, 'closing_amount', e.target.value)}
                            placeholder="Enter closing amount"
                          />
                        </td>
                        <td style={{ fontWeight: 'bold', color: pr.closing_amount !== '' && (parseFloat(pr.closing_amount) - parseFloat(pr.expected_amount)) < 0 ? '#ef4444' : pr.closing_amount !== '' && (parseFloat(pr.closing_amount) - parseFloat(pr.expected_amount)) > 0 ? '#f97316' : '#22c55e' }}>
                          {pr.closing_amount !== '' ? formatCurrency(parseFloat(pr.closing_amount) - parseFloat(pr.expected_amount)) : '-'}
                        </td>
                        <td>
                          <button className="ce-remove-btn" onClick={() => handleRemovePaymentReconciliation(index)} title="Remove">✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="ce-add-btn" onClick={handleAddPaymentReconciliation}>
                + Add Payment Mode
              </button>
            </div>

            {/* Summary + Submit Footer */}
            <div className="ce-footer-row">
              <div className="ce-summary-card">
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#1B1B29', marginBottom: '4px' }}>Session Summary</div>
                <div className="ce-summary-row">
                  <span className="ce-summary-label">Net Total</span>
                  <span className="ce-summary-value">{formatCurrency(netTotal)}</span>
                </div>
                <div className="ce-summary-row">
                  <span className="ce-summary-label">Total Quantity</span>
                  <span className="ce-summary-value">{totalQuantity || '0'}</span>
                </div>
                <div className="ce-summary-row">
                  <span className="ce-summary-label">Grand Total</span>
                  <span className="ce-summary-value highlight">{formatCurrency(grandTotal)}</span>
                </div>
              </div>
              <div className="ce-submit-panel">
                <button className="ce-submit-btn" onClick={handleSubmit} disabled={loading}>
                  {loading ? (
                    <>
                      <span style={{ display: 'inline-block', width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Submitting...
                    </>
                  ) : (
                    <>✓ Submit Closing Entry</>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="custom-modal-overlay">
          <div className="custom-logout-modal">
            <div className="modal-icon">
              <CircleAlert size={60} />
            </div>
            <h2 className="modal-title">Logout Confirmation</h2>
            <p className="modal-text">Are you sure you want to logout from the system?</p>
            <div className="modal-buttons">
              <button className="modal-btn confirm-btn" onClick={confirmLogout}>
                <Check size={20} /> YES
              </button>
              <button className="modal-btn cancel-btn" onClick={cancelLogout}>
                <X size={20} /> NO
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        .closing-entry-wrapper {
          width: 100%;
          min-height: 100vh;
          background-color: #F3F6FB;
          font-family: 'Inter', sans-serif;
        }

        .navbar-fixed-container {
          width: 100%;
          position: fixed;
          top: 0;
          left: 0;
          z-index: 2000;
          background-color: #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .navbar-placeholder {
          height: 80px;
          width: 100%;
          visibility: hidden;
          pointer-events: none;
        }

        .digital-icon {
          width: 34px;
          height: 34px;
          color: #555;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .navbar-nav { gap: 1.5rem; }

        .nav-link { 
          padding: 0.75rem 1rem; 
          cursor: pointer; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          position: relative; 
        }

        .nav-link:hover .digital-icon { 
          color: #604BE8;
          transform: translateY(-3px);
        }

        .nav-link.active .digital-icon { color: #604BE8; }

        .nav-link.active::after { 
          content: ''; 
          position: absolute; 
          bottom: 0; 
          left: 50%; 
          transform: translateX(-50%); 
          width: 34px; 
          height: 3px;
          background: #604BE8; 
          border-radius: 10px; 
        }

        @keyframes pulse-notif {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
          70% { transform: scale(1.1); box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        .navbar-logout-btn-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 10px;
        }

        .frontpage-header-logout {
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 6px;
          border-radius: 50%;
          transition: all 0.2s ease;
          background: #f3f4f6;
          margin-left: 10px;
          border: none;
          text-decoration: none;
        }

        .frontpage-header-logout:hover { background: #ef4444; transform: scale(1.1); }

        .logout-icon {
          width: 28px;
          height: 28px;
          color: #ef4444;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .frontpage-header-logout:hover .logout-icon { color: #ffffff; transform: scale(1.1); }

        .cursor-pointer { cursor: pointer; }

        /* ===== PREMIUM CONTENT AREA ===== */
        .closing-entry-container {
          padding: 32px 48px 48px;
          background-color: #F3F6FB;
        }

        .ce-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 28px;
        }

        .ce-page-header h1 {
          font-size: 28px;
          font-weight: 800;
          color: #1B1B29;
          margin: 0;
          letter-spacing: -0.5px;
        }

        .ce-subtitle {
          font-size: 11px;
          font-weight: 700;
          color: #604BE8;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin-bottom: 6px;
        }

        .ce-info-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 28px;
        }

        .ce-info-card {
          background: #ffffff;
          border-radius: 14px;
          padding: 20px 24px;
          border: 1px solid #E2E6EF;
          box-shadow: 0 4px 15px rgba(0,0,0,0.03);
          display: flex;
          flex-direction: column;
          gap: 8px;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .ce-info-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.06);
        }

        .ce-info-label {
          font-size: 11px;
          font-weight: 700;
          color: #8A94A6;
          text-transform: uppercase;
          letter-spacing: 0.8px;
        }

        .ce-info-select, .ce-info-input {
          width: 100%;
          border: 1px solid #E2E6EF;
          border-radius: 8px;
          padding: 9px 12px;
          font-size: 14px;
          font-weight: 500;
          color: #1B1B29;
          background: #ffffff;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .ce-info-select:focus, .ce-info-input:focus {
          border-color: #604BE8;
          box-shadow: 0 0 0 4px rgba(96, 75, 232, 0.1);
        }

        .ce-info-input:disabled {
          background: #F8F9FD;
          color: #8A94A6;
        }

        .ce-section-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #E2E6EF;
          box-shadow: 0 4px 20px rgba(0,0,0,0.03);
          margin-bottom: 24px;
        }

        .ce-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 2px solid #F3F6FB;
        }

        .ce-section-title {
          font-size: 16px;
          font-weight: 700;
          color: #1B1B29;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .ce-section-badge {
          background: #EEF0FD;
          color: #604BE8;
          font-size: 12px;
          font-weight: 700;
          padding: 2px 10px;
          border-radius: 20px;
        }

        .ce-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .ce-table thead tr { background: #F3F6FB; }

        .ce-table thead th {
          padding: 12px 16px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: #8A94A6;
          border-bottom: 1px solid #E2E6EF;
          white-space: nowrap;
        }

        .ce-table thead th:first-child { border-radius: 8px 0 0 0; }
        .ce-table thead th:last-child { border-radius: 0 8px 0 0; }

        .ce-table tbody tr {
          border-bottom: 1px solid #F3F6FB;
          transition: background 0.15s ease;
        }

        .ce-table tbody tr:hover { background: #FAFBFF; }
        .ce-table tbody tr:last-child { border-bottom: none; }

        .ce-table tbody td {
          padding: 12px 16px;
          font-size: 13px;
          font-weight: 500;
          color: #1B1B29;
          vertical-align: middle;
        }

        .ce-table-input {
          border: 1px solid #E2E6EF;
          border-radius: 8px;
          padding: 7px 12px;
          font-size: 13px;
          font-weight: 500;
          color: #1B1B29;
          width: 100%;
          background: #ffffff;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          min-width: 120px;
        }

        .ce-table-input:focus {
          border-color: #604BE8;
          box-shadow: 0 0 0 3px rgba(96,75,232,0.1);
        }

        .ce-table-input:disabled {
          background: #F8F9FD;
          color: #8A94A6;
        }

        .ce-amount-cell {
          font-weight: 700;
          color: #604BE8;
          font-size: 14px;
        }

        .ce-mode-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
          background: #EEF0FD;
          color: #604BE8;
        }

        .ce-remove-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: #FEE2E2;
          color: #ef4444;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13px;
          font-weight: 700;
        }

        .ce-remove-btn:hover {
          background: #ef4444;
          color: #ffffff;
          transform: scale(1.05);
        }

        .ce-add-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 18px;
          border-radius: 8px;
          border: 1.5px dashed #604BE8;
          background: transparent;
          color: #604BE8;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 12px;
        }

        .ce-add-btn:hover { background: #EEF0FD; }

        .ce-footer-row {
          display: flex;
          align-items: stretch;
          gap: 20px;
          margin-top: 8px;
        }

        .ce-summary-card {
          flex: 1;
          background: #ffffff;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #E2E6EF;
          box-shadow: 0 4px 20px rgba(0,0,0,0.03);
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .ce-summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #F3F6FB;
        }

        .ce-summary-row:last-child { border-bottom: none; }

        .ce-summary-label {
          font-size: 13px;
          font-weight: 600;
          color: #8A94A6;
        }

        .ce-summary-value {
          font-size: 16px;
          font-weight: 800;
          color: #1B1B29;
        }

        .ce-summary-value.highlight { color: #604BE8; }

        .ce-submit-panel {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: flex-end;
          gap: 12px;
          min-width: 240px;
        }

        .ce-submit-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 16px 36px;
          border-radius: 12px;
          border: none;
          background: linear-gradient(135deg, #604BE8 0%, #7C3AED 100%);
          color: #ffffff;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 15px rgba(96, 75, 232, 0.35);
          width: 100%;
          letter-spacing: 0.3px;
        }

        .ce-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(96, 75, 232, 0.45);
        }

        .ce-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .navbar-left-section { margin-right: 15px; }
        .back-nav-btn {
          background-color: transparent;
          border: 2px solid #604BE8;
          color: #604BE8;
          padding: 8px 20px;
          border-radius: 50px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          transition: all 0.3s ease;
          cursor: pointer;
          font-size: 16px;
          box-shadow: 0 2px 10px rgba(96, 75, 232, 0.1);
          margin-left: 10px;
        }
        .back-nav-btn:hover {
          background-color: #604BE8;
          color: #ffffff;
          transform: scale(1.05);
          box-shadow: 0 4px 15px rgba(96, 75, 232, 0.3);
        }
        
        .custom-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          z-index: 3000; animation: fadeIn 0.3s ease-out;
        }
        .custom-logout-modal {
          background: #ffffff; padding: 40px; border-radius: 24px;
          width: 90%; max-width: 400px; text-align: center;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3); border: 2px solid #ef4444;
          animation: slideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        .modal-icon { color: #ef4444; margin-bottom: 20px; display: flex; justify-content: center; }
        .modal-title { font-size: 1.5rem; font-weight: 800; color: #333; margin-bottom: 12px; }
        .modal-text { font-size: 1rem; color: #666; margin-bottom: 30px; line-height: 1.5; }
        .modal-buttons { display: flex; gap: 15px; justify-content: center; }
        .modal-btn { padding: 12px 25px; border-radius: 12px; font-weight: 700; font-size: 0.95rem; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; gap: 8px; border: none; }
        .confirm-btn { background: #ef4444; color: #ffffff; }
        .confirm-btn:hover { background: #dc2626; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4); }
        .cancel-btn { background: rgba(0, 0, 0, 0.05); color: #333; }
        .cancel-btn:hover { background: rgba(0, 0, 0, 0.1); transform: translateY(-2px); }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }

        @media (max-width: 991px) { 
          .navbar-placeholder { height: 100px; }
          .navbar-nav { gap: 0.5rem; }
          .back-nav-btn span { display: none; }
          .back-nav-btn { padding: 8px; }
          .digital-icon { width: 28px; height: 28px; }
          .logout-icon { width: 24px; height: 24px; }
          .frontpage-header-logout { margin-left: 5px; padding: 5px; }
          .closing-entry-container { padding: 20px 16px; }
          .ce-info-grid { grid-template-columns: repeat(2, 1fr); }
          .ce-footer-row { flex-direction: column; }
          .ce-submit-panel { min-width: unset; width: 100%; align-items: stretch; }
        }
      `}</style>
    </div>
  );
}

export default ClosingEntryWithNavbar;
